import React from 'react';
import type { UploadEntry } from './uploadTypes.js';
import { formatBytes, formatSpeed, formatEta, fileTypeIcon } from './portalUtils.js';

interface FileRowProps {
  entry: UploadEntry;
  onRetry: (id: string) => void;
}

function StatusPill({ status }: { status: UploadEntry['status'] }): React.ReactElement {
  if (status === 'complete') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
        style={{ background: 'rgba(1, 181, 116, 0.18)', color: 'var(--accent-green)', border: '1px solid rgba(1,181,116,0.3)' }}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2 6 5 9 10 3" />
        </svg>
        Complete
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
        style={{ background: 'rgba(238, 93, 80, 0.18)', color: 'var(--accent-red)', border: '1px solid rgba(238,93,80,0.3)' }}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="2" y1="2" x2="10" y2="10" />
          <line x1="10" y1="2" x2="2" y2="10" />
        </svg>
        Failed
      </span>
    );
  }
  if (status === 'uploading') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
        style={{ background: 'rgba(13, 207, 184, 0.15)', color: 'var(--accent-blue)', border: '1px solid rgba(13,207,184,0.3)' }}
      >
        <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent-blue)' }} />
        Uploading
      </span>
    );
  }
  // queued
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
      style={{ background: 'rgba(106, 148, 150, 0.15)', color: 'var(--text-dimmed)', border: '1px solid var(--border)' }}
    >
      Queued
    </span>
  );
}

export function FileRow({ entry, onRetry }: FileRowProps): React.ReactElement {
  const pct = entry.file.size > 0 ? Math.round((entry.bytesUploaded / entry.file.size) * 100) : 0;
  const isActive = entry.status === 'uploading';

  return (
    <div
      className="rounded-xl p-3 transition-all duration-200"
      style={{
        background: 'var(--bg-tertiary)',
        border: '1px solid var(--border)',
        boxShadow: isActive ? '0 0 0 1px rgba(13,207,184,0.2)' : 'none',
      }}
    >
      <div className="flex items-start gap-3 min-w-0">
        {/* File type icon */}
        <div
          className="flex items-center justify-center rounded-lg shrink-0 text-lg"
          style={{ width: '38px', height: '38px', background: 'var(--glass-bg)', border: '1px solid var(--border)' }}
          aria-hidden="true"
        >
          {fileTypeIcon(entry.file.name)}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: name + status pill */}
          <div className="flex items-center gap-2 justify-between mb-1.5">
            <span
              className="text-sm font-medium truncate"
              style={{ color: 'var(--text-primary)', maxWidth: '60%' }}
              title={entry.file.name}
            >
              {entry.file.name}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <StatusPill status={entry.status} />
              {entry.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => onRetry(entry.id)}
                  className="rounded-lg px-2.5 py-0.5 text-[11px] font-semibold transition-opacity hover:opacity-80"
                  style={{
                    background: 'rgba(238, 93, 80, 0.20)',
                    color: 'var(--accent-red)',
                    border: '1px solid rgba(238,93,80,0.3)',
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div
            className="h-1.5 rounded-full overflow-hidden mb-1.5"
            style={{ background: 'var(--glass-bg)' }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Upload progress for ${entry.file.name}`}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${pct}%`,
                background: entry.status === 'complete'
                  ? 'var(--accent-green)'
                  : entry.status === 'failed'
                    ? 'var(--accent-red)'
                    : 'linear-gradient(90deg, #0dcfb8 0%, #06b6d4 100%)',
              }}
            />
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-3 text-[11px] flex-wrap" style={{ color: 'var(--text-dimmed)' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatBytes(entry.bytesUploaded)} / {formatBytes(entry.file.size)}
            </span>
            {isActive && entry.speedBps > 0 && (
              <>
                <span style={{ color: 'var(--text-secondary)' }}>·</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatSpeed(entry.speedBps)}
                </span>
              </>
            )}
            {isActive && entry.etaSec > 0 && (
              <>
                <span style={{ color: 'var(--text-secondary)' }}>·</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatEta(entry.etaSec)}
                </span>
              </>
            )}
            {entry.status === 'failed' && entry.errorMessage && (
              <>
                <span style={{ color: 'var(--text-secondary)' }}>·</span>
                <span style={{ color: 'var(--accent-red)' }}>{entry.errorMessage}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
